import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Mail, Upload, Send, Loader2, FileSpreadsheet, Eye, Trash2, Beaker, X,
  CheckCircle2, AlertTriangle, Play,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_BODY = `<p>Hi {{first_name}},</p>
<p>I noticed {{company}} runs freight across {{lanes}}. We help shippers like you tighten dispatch and carrier compliance without taking a broker cut — pure SaaS, flat monthly fee.</p>
<p>Worth a 15-min call this week?</p>
<p>— Givi, Tempus Dispatch</p>`;

const renderMerge = (tpl, ctx) =>
  (tpl || '').replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, k) => ctx?.[k] ?? '');

const CampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [active, setActive] = useState(null);            // selected campaign
  const [activeDetail, setActiveDetail] = useState(null); // detailed view incl. recipients
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('Quick question about freight at {{company}}');
  const [body, setBody] = useState(DEFAULT_BODY);
  const [recipients, setRecipients] = useState([]);
  const [csvFields, setCsvFields] = useState([]);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const fileRef = useRef(null);

  const loadCampaigns = async () => {
    try {
      const r = await axios.get(`${API}/campaigns?limit=30`);
      setCampaigns(r.data || []);
    } catch { /* ignore */ }
  };

  const loadDetail = async (cid) => {
    try {
      const r = await axios.get(`${API}/campaigns/${cid}`);
      setActiveDetail(r.data);
      setActive(r.data);
    } catch {
      toast.error('Failed to load campaign');
    }
  };

  useEffect(() => { loadCampaigns(); }, []);
  useEffect(() => {
    if (!active?.id) return;
    if (active.status !== 'sending') return;
    const t = setInterval(() => loadDetail(active.id), 4000);
    return () => clearInterval(t);
  }, [active?.id, active?.status]);

  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/campaigns/upload-csv`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRecipients(r.data.rows || []);
      setCsvFields(r.data.fields || []);
      toast.success(`Loaded ${r.data.count} recipients (skipped ${r.data.skipped})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CSV upload failed');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const create = async () => {
    if (!name.trim()) return toast.error('Campaign name required');
    if (!recipients.length) return toast.error('Upload a CSV first');
    setCreating(true);
    try {
      const r = await axios.post(`${API}/campaigns`, {
        name, subject, html_body: body, recipients,
      });
      toast.success(`Campaign created — ${r.data.recipient_count} recipients`);
      setName(''); setRecipients([]); setCsvFields([]);
      await loadCampaigns();
      await loadDetail(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const testSend = async () => {
    if (!active) return;
    if (!testEmail.trim()) return toast.error('Enter test email');
    try {
      const sample = activeDetail?.recipients?.[0]?.merge_data || {};
      const r = await axios.post(`${API}/campaigns/${active.id}/test`, {
        to_email: testEmail, recipient_data: sample,
      });
      if (r.data.sent) toast.success(`Test sent to ${testEmail}`);
      else if (r.data.suppressed) toast.warning('Recipient suppressed — not sent');
      else toast.error(r.data.error || 'Send failed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    }
  };

  const startSend = async () => {
    if (!active) return;
    if (!window.confirm(`Send "${active.name}" to ${active.recipient_count} recipients?`)) return;
    setSending(true);
    try {
      await axios.post(`${API}/campaigns/${active.id}/send`, {});
      toast.success('Send queued');
      await loadDetail(active.id);
      await loadCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const cancel = async () => {
    if (!active) return;
    try {
      await axios.post(`${API}/campaigns/${active.id}/cancel`);
      toast.success('Campaign cancelled');
      await loadDetail(active.id);
      await loadCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    }
  };

  const sampleCtx = recipients[0] || {};
  const renderedSubject = renderMerge(subject, sampleCtx);
  const renderedBody = renderMerge(body, sampleCtx);

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6" data-testid="campaigns-page">
        <div>
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-400" />
            <h1 className="text-3xl font-bold tracking-tight">Cold Email Campaigns</h1>
            <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px]">CAN-SPAM compliant</Badge>
          </div>
          <p className="text-muted-foreground text-sm mt-1 max-w-3xl">
            Upload a CSV, write a templated email (merge tags like <code className="text-amber-300">{'{{first_name}}'}</code>),
            and send via SES with automatic suppression-list filtering, physical-address footer, and open-tracking pixel.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Composer */}
          <Card className="lg:col-span-3" data-testid="campaign-composer-card">
            <CardHeader className="pb-3"><CardTitle className="text-base">New campaign</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Field label="Campaign name">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Q1 mid-market shippers"
                  data-testid="campaign-name-input" />
              </Field>
              <Field label="Subject">
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="campaign-subject-input" />
              </Field>
              <Field label="HTML body (use {{merge_tags}})">
                <Textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)}
                  className="font-mono text-xs" data-testid="campaign-body-input" />
              </Field>

              <div className="border border-border rounded-md p-3 bg-muted/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <FileSpreadsheet className="w-4 h-4 text-emerald-400" /> Recipients
                    {recipients.length > 0 && (
                      <Badge variant="outline" data-testid="csv-row-count">{recipients.length}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input ref={fileRef} type="file" accept=".csv" onChange={onCsv} className="hidden" data-testid="csv-file-input" />
                    <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="upload-csv-btn">
                      <Upload className="w-3.5 h-3.5 mr-1" /> Upload CSV
                    </Button>
                    {recipients.length > 0 && (
                      <Button size="sm" variant="ghost" onClick={() => { setRecipients([]); setCsvFields([]); }} data-testid="clear-csv-btn">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                {csvFields.length > 0 ? (
                  <div className="text-[11px] text-muted-foreground" data-testid="csv-fields">
                    Available merge tags:{' '}
                    {csvFields.map((f) => (
                      <code key={f} className="ml-1 px-1 rounded bg-zinc-800 text-amber-300">{`{{${f}}}`}</code>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    CSV must include an <code className="text-amber-300">email</code> column. Other columns become merge tags.
                  </p>
                )}
              </div>

              <Button
                onClick={create}
                disabled={creating || !name.trim() || !recipients.length}
                className="w-full"
                data-testid="create-campaign-btn"
              >
                {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating</>
                          : <><CheckCircle2 className="w-4 h-4 mr-2" /> Create draft</>}
              </Button>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card className="lg:col-span-2" data-testid="campaign-preview-card">
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2">
              <Eye className="w-4 h-4" /> Preview {recipients[0]?.email && <span className="text-[11px] font-normal text-muted-foreground">(as {recipients[0].email})</span>}
            </CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="text-xs text-muted-foreground">Subject</div>
              <div className="text-sm font-medium break-words" data-testid="preview-subject">{renderedSubject || <span className="italic text-muted-foreground">empty</span>}</div>
              <div className="text-xs text-muted-foreground pt-2">Body</div>
              <iframe
                title="campaign-preview"
                srcDoc={`<html><body style="font-family:Arial,sans-serif;color:#111;font-size:13px;line-height:1.5;padding:14px;">${renderedBody}</body></html>`}
                data-testid="campaign-preview-iframe"
                className="w-full border border-border rounded-md bg-white"
                style={{ height: '320px' }}
              />
            </CardContent>
          </Card>
        </div>

        {/* Selected campaign panel */}
        {active && (
          <Card data-testid="active-campaign-card">
            <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-base flex items-center gap-2">
                {active.name}
                <Badge className={statusColor(active.status)} data-testid="campaign-status">{active.status}</Badge>
              </CardTitle>
              <div className="flex flex-wrap gap-2 items-center">
                <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@example.com" className="w-56"
                  data-testid="test-email-input" />
                <Button size="sm" variant="outline" onClick={testSend} data-testid="test-send-btn">
                  <Beaker className="w-3.5 h-3.5 mr-1" /> Test send
                </Button>
                {(active.status === 'draft') && (
                  <Button size="sm" onClick={startSend} disabled={sending} data-testid="start-send-btn">
                    {sending ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Queuing</>
                             : <><Send className="w-3.5 h-3.5 mr-1" /> Send to all ({active.recipient_count})</>}
                  </Button>
                )}
                {(active.status === 'sending') && (
                  <Button size="sm" variant="destructive" onClick={cancel} data-testid="cancel-btn">
                    <X className="w-3.5 h-3.5 mr-1" /> Cancel
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <Stat label="Recipients" value={active.recipient_count} />
                <Stat label="Sent" value={active.sent_count} color="text-green-400" />
                <Stat label="Suppressed" value={active.suppressed_count} color="text-zinc-400" />
                <Stat label="Failed" value={active.failed_count} color="text-red-400" />
                <Stat label="Opens" value={active.open_count} color="text-amber-400" />
              </div>
              {activeDetail?.recipients?.length > 0 && (
                <div className="mt-4 border border-border rounded-md max-h-72 overflow-auto" data-testid="recipients-table">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 sticky top-0">
                      <tr className="text-left">
                        <th className="p-2">Email</th><th className="p-2">Status</th><th className="p-2">Opens</th><th className="p-2">Sent at</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeDetail.recipients.slice(0, 200).map((r) => (
                        <tr key={r.id} className="border-t border-border/60">
                          <td className="p-2 truncate max-w-[260px]">{r.email}</td>
                          <td className="p-2">
                            <span className={statusColor(r.status)}>{r.status}</span>
                          </td>
                          <td className="p-2 tabular-nums">{r.open_count || 0}</td>
                          <td className="p-2 text-muted-foreground">{r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* History */}
        <Card data-testid="campaigns-history-card">
          <CardHeader className="pb-3"><CardTitle className="text-base">Recent campaigns</CardTitle></CardHeader>
          <CardContent className="px-0">
            {campaigns.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">No campaigns yet.</p>
            ) : (
              <div className="divide-y divide-border">
                {campaigns.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => loadDetail(c.id)}
                    className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-muted/30"
                    data-testid={`campaign-row-${c.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {c.subject} · {c.recipient_count} recipients
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      sent {c.sent_count}/{c.recipient_count}
                    </span>
                    <Badge className={statusColor(c.status)}>{c.status}</Badge>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-[11px] text-muted-foreground flex items-start gap-2 max-w-3xl">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0 text-amber-400" />
          <span>
            Every email automatically includes the CAN-SPAM unsubscribe link + physical postal address.
            Suppressed addresses are skipped silently. While AWS SES is in sandbox mode, only verified
            addresses will receive mail — see <code>/admin/setup-status</code> for status.
          </span>
        </div>
      </div>
    </Layout>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);

const Stat = ({ label, value, color = 'text-foreground' }) => (
  <div className="border border-border rounded-md p-3">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`text-xl font-semibold tabular-nums ${color}`}>{value ?? 0}</div>
  </div>
);

const statusColor = (s) => {
  switch (s) {
    case 'sent':
    case 'done': return 'bg-green-500/15 text-green-300 border-green-500/30';
    case 'sending': return 'bg-blue-500/15 text-blue-300 border-blue-500/30';
    case 'suppressed': return 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30';
    case 'failed': return 'bg-red-500/15 text-red-300 border-red-500/30';
    case 'cancelled': return 'bg-orange-500/15 text-orange-300 border-orange-500/30';
    case 'queued':
    case 'draft':
    default: return 'bg-zinc-700/40 text-zinc-300 border-zinc-600';
  }
};

export default CampaignsPage;
